import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DatePickerComponent } from 'src/app/shared/date-picker/date-picker.component';

@Component({
  selector: 'app-custom-datepicker',
  templateUrl: './custom-datepicker.component.html',
  styleUrls: ['./custom-datepicker.component.scss'],
})
export class CustomDatepickerComponent implements OnInit, OnChanges, OnDestroy {
  @Output() dateChange = new EventEmitter<string>();
  @Input() startDate: string;
  @Input() vAlignment = '';
  @Input() deleteMarginTop: boolean;
  @Input() adminView = true;
  @ViewChild(DatePickerComponent) datePickerCmp: DatePickerComponent;
  displayedDate: Date;
  minDate = new Date();
  /** Internal control backing the hidden app-date-picker. User picks flow
   *  through valueChanges → onDateChange; programmatic writes use
   *  emitEvent: false so only calendar selections emit. */
  dateControl = new FormControl(null);
  private destroyed$ = new Subject<void>();
  constructor(
    private datePipe: DatePipe,
    private cd: ChangeDetectorRef,
  ) {}
  date: Date;
  formatDate(date: Date): string {
    return this.datePipe.transform(date, 'yyyy-MM-dd');
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.startDate && changes.startDate.currentValue) {
      this.startDate = changes.startDate.currentValue;
      this.displayedDate = this.parseISOString(this.startDate);
      this.date = this.displayedDate;
      this.dateControl.setValue(this.date, { emitEvent: false });
      this.cd.detectChanges();
    }
  }
  ngOnInit() {
    this.minDate.setHours(0, 0, 0, 0);
    this.date =
      this.startDate != null && this.startDate != ''
        ? new Date(this.startDate)
        : new Date();
    if (this.startDate) {
      this.displayedDate = this.parseISOString(this.startDate);
      this.date = this.displayedDate;
      this.cd.detectChanges();
    }
    this.dateControl.setValue(this.date, { emitEvent: false });
    this.dateControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((value) => this.onDateChange({ value }));
    this.dateChange.emit(this.formatDate(this.date));
  }
  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
  /** Opens the hidden date picker's calendar overlay. */
  openPicker(): void {
    this.datePickerCmp?.open();
  }
  onDateChange(event: any) {
    const selected = new Date(event.value);
    // The shared date-picker has no [min]; enforce the customer-view lower
    // bound here instead: dates before today are selectable in the calendar
    // UI but rejected (the previous date is restored).
    if (this.adminView != true && selected < this.minDate) {
      this.dateControl.setValue(this.date, { emitEvent: false });
      return;
    }
    this.date = selected;
    this.dateControl.setValue(this.date, { emitEvent: false });
    this.dateChange.emit(this.formatDate(this.date));
    this.cd.detectChanges();
  }
  parseISOString(isoDate: string): Date {
    // Parse ISO date string directly without timezone offset manipulation
    // This ensures dates are handled correctly regardless of timezone
    const cleanedIsoDate = isoDate.replace('Z', '');
    const date = new Date(cleanedIsoDate);
    return date;
  }
  incrementDate() {
    this.date = new Date(this.date.setDate(this.date.getDate() + 1));
    this.dateControl.setValue(this.date, { emitEvent: false });
    this.dateChange.emit(this.formatDate(this.date));
    this.cd.detectChanges();
  }

  decrementDate() {
    let tomorrow = new Date(this.date);
    tomorrow.setDate(tomorrow.getDate() - 1);
    if (this.adminView == true) {
      this.date = new Date(this.date.setDate(this.date.getDate() - 1));
      this.dateControl.setValue(this.date, { emitEvent: false });
      this.dateChange.emit(this.formatDate(this.date));
    } else if (tomorrow >= this.minDate) {
      this.date = new Date(this.date.setDate(this.date.getDate() - 1));
      this.dateControl.setValue(this.date, { emitEvent: false });
      this.dateChange.emit(this.formatDate(this.date));
    }
    this.cd.detectChanges();
  }
}
